import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { ResponseLogin, User } from '../../../../common/iam/users/types';
import { ApiError } from '../../../../common/types-api';
import { isTeamUser } from '../../../../common/utils';
import { fetchOwnInfo, forgotPassword, login, logout, resetPassword, signup } from '../../../api/iam/users';
import { history } from '../../../common/generic/utilities/history';
import { clearStorageExceptUiComponentStatuses, saveModelVersion } from '../../../common/generic/utilities/storage';
import { StateTree } from './types';
import { handleSagaException } from '../../../common/generic/utilities/utils';
import {
  LoadUserInfoWithTokenAction,
  loggedIn,
  loggedOut,
  requestFailed,
  requestStarted,
  requestSucceeded,
  SubmitForgotPasswordAction,
  SubmitLoginAction,
  SubmitResetPasswordAction,
  SubmitSignupAction,
} from '../actions/actions';
import contributionDataRootSaga from '../modules/contribution/sagas';

const SERVICE_UNAVAILABLE = 'Service temporarily unavailable';

function* submitLogin({ payload: { email, password, requestId } }: SubmitLoginAction) {
  clearStorageExceptUiComponentStatuses();
  yield put(requestStarted(requestId));
  try {
    const { data: user, apiError } = (yield call(login, email, password)) as {
      data: User;
      apiError: ApiError | undefined;
    };
    if (apiError) {
      const errorMessage = getErrorMessage(apiError);
      yield put(requestFailed(requestId, errorMessage));
    } else {
      yield put(loggedIn(user));
      if (isTeamUser(user)) {
        saveModelVersion('master');
      }
      yield put(requestSucceeded(requestId));
    }
  } catch (e) {
    yield handleSagaException(e as Error, requestId, SERVICE_UNAVAILABLE);
  }
}

function getErrorMessage(response: ApiError) {
  if (response.status && response.status !== 200) {
    const message = response.message ? response.message : response.statusText;
    if (response.status === 502) {
      // Bad gateway, cloundfront's way of saying our server is down
      console.error('Bad gateway from the API', message);
      return SERVICE_UNAVAILABLE;
    }
    return message;
  }
  return '';
}

function* submitForgotPassword({ payload: { email, requestId } }: SubmitForgotPasswordAction) {
  yield put(requestStarted(requestId));
  try {
    const { apiError } = (yield call(forgotPassword, email)) as {
      apiError: ApiError | undefined;
    };
    if (apiError) {
      const errorMessage = getErrorMessage(apiError);
      yield put(requestFailed(requestId, errorMessage));
    } else {
      yield put(requestSucceeded(requestId));
    }
  } catch (e) {
    yield handleSagaException(e as Error, requestId);
  }
}

function* submitResetPassword({ payload: { password, code, requestId } }: SubmitResetPasswordAction) {
  yield put(requestStarted(requestId));
  try {
    const { apiError } = (yield call(resetPassword, code, password)) as {
      apiError: ApiError | undefined;
    };
    if (apiError) {
      const errorMessage = getErrorMessage(apiError);
      yield put(requestFailed(requestId, errorMessage));
    } else {
      yield put(requestSucceeded(requestId));
    }
  } catch (e) {
    yield handleSagaException(e as Error, requestId);
  }
}

function* submitSignup({ payload: { requestId, ...signupData } }: SubmitSignupAction) {
  yield put(requestStarted(requestId));
  try {
    // PLT-4720: the JWT lives in an HttpOnly cookie now and is not
    // readable from JS. Use the Redux user state as the source of truth
    // for "is somebody already logged in here" — equivalent to the prior
    // `getAccessToken()` check, since the user state is hydrated from
    // `/api/own-info` on app boot and cleared on logout.
    const existingUser = (yield select((s: StateTree) => s.user.user)) as User | undefined;
    const { data, apiError } = (yield call(signup, signupData)) as {
      data: ResponseLogin;
      apiError: ApiError | undefined;
    };
    if (apiError) {
      const errorMessage = getErrorMessage(apiError);
      yield put(requestFailed(requestId, errorMessage.indexOf('email') > -1 ? 'Invalid email' : errorMessage));
    } else {
      if (!existingUser) {
        clearStorageExceptUiComponentStatuses();
        yield put(loggedIn(data));
      }
      yield put(requestSucceeded(requestId));
    }
  } catch (e) {
    yield handleSagaException(e as Error, requestId);
  }
}

function* getInfoWithToken({ payload: { requestId } }: LoadUserInfoWithTokenAction) {
  yield put(requestStarted(requestId));
  try {
    // PLT-4720: the JWT lives in an HttpOnly `up-access` cookie now. The
    // browser carries it automatically via `credentials: 'include'` set
    // by the fetcher, and `fetchOwnInfo` already takes no token arg.
    const { data: user, apiError } = (yield call(fetchOwnInfo)) as {
      data: User;
      apiError: ApiError | undefined;
    };

    if (apiError) {
      yield put(requestFailed(requestId, apiError.statusText));
      // PLT-4720: this hook now fires unconditionally on app boot (we no
      // longer have a JS-readable token to gate on). A 401 here means
      // "no valid session cookie" — for an anonymous visitor or someone
      // on `/sign-in/<code>` that is the expected state, not a reason to
      // forcibly log out. Only call `logOut` when the user was already
      // signed in in the Redux store: in that case the cookie has gone
      // stale mid-session and the saga should clean up.
      if (apiError.status === 401) {
        const existingUser = (yield select((s: StateTree) => s.user.user)) as User | undefined;
        if (existingUser) {
          yield call(logOut);
        }
      }
    } else {
      yield put(loggedIn(user));
      yield put(requestSucceeded(requestId));
    }
  } catch (e) {
    yield handleSagaException(e as Error, requestId);
  }
}

function* logOut() {
  try {
    // Backend logout
    yield call(logout);
  } catch (e) {
    // Ignore any error on logout call
  }

  clearStorageExceptUiComponentStatuses();
  yield put(loggedOut());
  yield call([history, 'push'], '/');
  yield call([history, 'go'], 0);
}

function* logOutWithoutNavigation() {
  try {
    // Backend logout
    yield call(logout);
  } catch (e) {
    // Ignore any error on logout call
  }

  clearStorageExceptUiComponentStatuses();
  yield put(loggedOut());
  // No navigation - let the component handle it
}

function* userRootSaga() {
  yield takeLatest('USER/SUBMIT_LOGIN', submitLogin);
  yield takeLatest('USER/SUBMIT_FORGOT_PASSWORD', submitForgotPassword);
  yield takeLatest('USER/SUBMIT_RESET_PASSWORD', submitResetPassword);
  yield takeLatest('USER/SUBMIT_SIGNUP', submitSignup);
  yield takeLatest('USER/GET_INFO_WITH_TOKEN', getInfoWithToken);
  yield takeEvery('USER/LOG_OUT', logOut);
  yield takeEvery('USER/LOG_OUT_WITHOUT_NAVIGATION', logOutWithoutNavigation);
}

export default function* rootSaga() {
  yield all([userRootSaga(), contributionDataRootSaga()]);
}
